// Dark Requiem: The Corruption Within — Full Narrative
// Canon: Jack MERCER, David Mercer, Dr. Miriam Mercer, Dr. Elias Voss,
// Orpheus Biomedical, Project Requiem, Subject J-14, The Corruption.
// 12 chapters across 3 acts, plus 4 endings and optional memory sequences.

const IMG = {
  facility: "https://images.pexels.com/photos/4012056/pexels-photo-4012056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  red: "https://images.unsplash.com/photo-1658713981148-8b2fe5e49c6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjBmaWxtJTIwZ3JhaW4lMjByZWQlMjBlbWVyZ2VuY3klMjBsaWdodCUyMGNvcnJpZG9yfGVufDB8fHx8MTc4NTAyNTYzN3ww&ixlib=rb-4.1.0&q=85",
  paper: "https://images.pexels.com/photos/235985/pexels-photo-235985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

// helper factory for choice effects
const fx = (o) => o;

export const SCENES = {
  // ============================================================
  // ACT ONE — THE DISCOVERY
  // ============================================================

  // ----- CHAPTER 1: The Silent Grounds -----
  start: {
    act: 1, chapter: "Ch. 1 — The Silent Grounds", bg: IMG.facility,
    text: [
      "11:17 PM. The forest around Blackwood Research Annex has forgotten how to make noise.",
      "You are fourteen years old. Your name is Jack Mercer. Your father David has not come home.",
      "His last voicemail was static, an alarm, and one broken sentence: whatever you hear, it isn't us.",
    ],
    choices: [
      { label: "Try the front gate", next: "gate" },
      { label: "Circle to the drainage channel your dad once mentioned", next: "drainage",
        effect: fx({ addJournal: "Perimeter — the drainage grate is loose. Dad talked about it once.", composure: +3 }) },
    ],
  },
  gate: {
    act: 1, chapter: "Ch. 1 — The Silent Grounds", bg: IMG.facility,
    text: [
      "The main gate is chained. A security camera above the arch tracks you slowly, even though its status light is dead.",
      "The gate cannot be climbed without being seen. But there is a drainage channel to the south.",
    ],
    choices: [
      { label: "Take the drainage channel", next: "drainage", effect: fx({ addJournal: "The cameras still see." }) },
    ],
  },
  drainage: {
    act: 1, chapter: "Ch. 1 — The Silent Grounds", bg: IMG.facility,
    text: [
      "You slip through the storm grate. The tunnel smells of iron and wet leaves.",
      "You find Dad's truck parked crooked in the loading yard. The driver door is open. His thermos is still warm.",
    ],
    choices: [
      { label: "Take the flashlight from the truck", next: "ch2_lobby",
        effect: fx({ addItem: "Flashlight", addJournal: "Took Dad's flashlight. Half battery.", composure: +2 }) },
      { label: "Leave the truck. Push into the building.", next: "ch2_lobby",
        effect: fx({ sanity: -3 }) },
    ],
  },

  // ----- CHAPTER 2: Last Call -----
  ch2_lobby: {
    act: 1, chapter: "Ch. 2 — Last Call", bg: IMG.facility,
    text: [
      "Administration Wing. A phone is ringing at the security desk.",
      "You follow the sound. You already know whose phone it is.",
    ],
    choices: [
      { label: "Answer the phone", next: "ch2_body", effect: fx({ sanity: -4 }) },
      { label: "Approach the desk directly", next: "ch2_body" },
    ],
  },
  ch2_body: {
    act: 1, chapter: "Ch. 2 — Last Call", bg: IMG.facility, mode: "grief",
    text: [
      "David Mercer is slumped beside the security console. His face is not damaged. It is emptied.",
      "One hand holds a broken control device. The other holds a small brass key stamped with an unfamiliar symbol — a circle bisected by a wound.",
      "You do not cry. You should. Something in your chest refuses to move.",
    ],
    choices: [
      { label: "Take the key and his pocket watch", next: "ch2_lockdown",
        effect: fx({ addItem: "Requiem Key", addItem2: "Dad's Pocket Watch", addJournal: "COLLECTIBLE — Family Keepsake: Pocket Watch.",
                    setFlag: { hasKey: true, keepsake_watch: true }, collectible: "keepsake", sanity: -14, composure: -20 }) },
      { label: "Just take the key. Leave the watch.", next: "ch2_lockdown",
        effect: fx({ addItem: "Requiem Key", setFlag: { hasKey: true }, sanity: -18, composure: -25, vengeance: +1 }) },
    ],
  },
  ch2_lockdown: {
    act: 1, chapter: "Ch. 2 — Last Call", bg: IMG.red, mode: "danger",
    text: [
      "Sirens. Steel bulkheads drop from the ceiling. LOCKDOWN — ORPHEUS PROTOCOL 7.",
      "Behind a wall of reinforced glass, something twelve feet tall turns to look at you. One arm is longer than the other.",
      "Its face — for one second — is a man you have never met. Then the glass detonates outward.",
    ],
    choices: [
      { label: "Vault the desk and run for the records room", next: "ch2_chase", effect: fx({ sanity: -8, hallucinate: "monster" }) },
      { label: "Slide beneath the closing security door", next: "ch2_chase",
        effect: fx({ sanity: -6, addJournal: "First contact — Subject Voss. He is not a man anymore." }) },
    ],
  },
  ch2_chase: {
    act: 1, chapter: "Ch. 2 — Last Call", bg: IMG.red, mode: "danger",
    text: [
      "You crash into the records room. A cabinet drawer stands open like a mouth.",
      "You climb inside. You hold your breath.",
      "It drags itself past you, whispering a name in your father's voice: '...Jack...'",
    ],
    choices: [
      { label: "Wait until it moves on. Look for the workshop.", next: "ch3_workshop",
        effect: fx({ addJournal: "It knows my name.", composure: -18 }) },
    ],
  },

  // ----- CHAPTER 3: David's Workshop -----
  ch3_workshop: {
    act: 1, chapter: "Ch. 3 — David's Workshop", bg: IMG.facility, mode: "safe",
    text: [
      "You find it: a small workshop wired with analog switches, faraday mesh, and a hand-lettered sign: STAY HERE, JACK.",
      "Dad's recorder blinks red. You press play.",
      "'If you're hearing this — I failed. There's an emergency transit tunnel under the medical wing. To reach it, you need three power sectors online. Don't trust voices. Not mine. Not your mother's. Nothing here is us anymore.'",
    ],
    choices: [
      { label: "Take Dad's multitool and family photo", next: "ch3_end",
        effect: fx({ addItem: "Dad's Multitool", addJournal: "COLLECTIBLE — Family Keepsake: Photo of Mom and Dad.",
                    setFlag: { hasMultitool: true, keepsake_photo: true }, collectible: "keepsake", composure: +12, sanity: +5 }) },
      { label: "Scavenge components first", next: "ch3_end",
        effect: fx({ addItem: "Copper Wire", addItem2: "Battery Cell", addItem3: "Scrap Metal",
                    addJournal: "COLLECTIBLE — David's Instructions: Basic Trap Set.", setFlag: { instr_traps: true }, collectible: "instruction" }) },
    ],
  },
  ch3_end: {
    act: 1, chapter: "Ch. 3 — David's Workshop", bg: IMG.facility, mode: "safe",
    text: [
      "You study the photograph. Your parents on a beach you don't remember. You were three.",
      "The workshop lights flicker. Somewhere in the wall, a woman's voice — impossibly familiar — whispers:",
      "'He brought you back to us.'",
    ],
    choices: [
      { label: "Take the tools. Move on.", next: "ch4_grid",
        effect: fx({ act: 2, hallucinate: "mother", composure: -8 }) },
    ],
  },

  // ============================================================
  // ACT TWO — THE HUNT
  // ============================================================

  // ----- CHAPTER 4: The Broken Grid -----
  ch4_grid: {
    act: 2, chapter: "Ch. 4 — The Broken Grid", bg: IMG.facility,
    text: [
      "Maintenance Sector. Generators the size of trucks. Two are dark, one is bleeding sparks.",
      "You have to bring both back online without waking what patrols the tunnels above.",
    ],
    choices: [
      { label: "Craft a Shock Trap (Wire + Battery)", requires: ["Copper Wire", "Battery Cell"], next: "ch4_gen1",
        effect: fx({ craft: "Shock Trap", consume: ["Copper Wire", "Battery Cell"], addJournal: "Crafted Shock Trap." }) },
      { label: "Craft a Noise Decoy (Scrap Metal)", requires: ["Scrap Metal"], next: "ch4_gen1",
        effect: fx({ craft: "Noise Decoy", consume: ["Scrap Metal"], addJournal: "Crafted Noise Decoy." }) },
      { label: "Skip crafting. Move fast.", next: "ch4_gen1", effect: fx({ sanity: -4 }) },
    ],
  },
  ch4_gen1: {
    act: 2, chapter: "Ch. 4 — The Broken Grid", bg: IMG.facility, mode: "stealth",
    text: [
      "Generator North. You reroute the coupling. Sparks light the room like a heart being restarted.",
      "The Corruption drops from a ceiling vent behind the reactor block. It is faster now.",
    ],
    choices: [
      { label: "Deploy the Noise Decoy", requires_item: "Noise Decoy", next: "ch4_gen2",
        effect: fx({ consume: ["Noise Decoy"], addJournal: "Decoy pulled it into the boiler room.", setFlag: { gridN: true } }) },
      { label: "Deploy the Shock Trap", requires_item: "Shock Trap", next: "ch4_gen2",
        effect: fx({ consume: ["Shock Trap"], addJournal: "Shock trap detonated. Bought thirty seconds.", setFlag: { gridN: true }, sanity: -4 }) },
      { label: "Hide in the coolant pipe and hold your breath", next: "ch4_gen2",
        effect: fx({ setFlag: { gridN: true }, sanity: -10, composure: -8 }) },
    ],
  },
  ch4_gen2: {
    act: 2, chapter: "Ch. 4 — The Broken Grid", bg: IMG.red, mode: "danger",
    text: [
      "Generator South. It sees you. Rage Mode — every door in the sector shakes at once.",
      "You throw the breaker. Power floods the hallway. The transit sign glows white.",
      "The floor gives out beneath you.",
    ],
    choices: [
      { label: "Ride the collapsing service tunnel into Medical", next: "ch5_medical",
        effect: fx({ setFlag: { gridS: true }, sanity: -12, composure: -10 }) },
    ],
  },

  // ----- CHAPTER 5: The Patients -----
  ch5_medical: {
    act: 2, chapter: "Ch. 5 — The Patients", bg: IMG.facility,
    text: [
      "Medical Wing. Restraints on every bed. A wall of black-and-white monitors, all showing the same recording of a boy — you? — at three years old.",
      "Patient logs describe dreams shared across cells. The same dream, every night: a garden that speaks.",
    ],
    choices: [
      { label: "Read Patient Testimony Log #7", next: "ch5_memory",
        effect: fx({ addJournal: "COLLECTIBLE — Patient Testimony #7: 'The garden speaks. It said my daughter's name.'",
                    setFlag: { patient_log_7: true }, collectible: "patient", sanity: -6 }) },
      { label: "Ignore the logs. Move on.", next: "ch5_memory", effect: fx({ sanity: -3, vengeance: +1 }) },
    ],
  },
  ch5_memory: {
    act: 2, chapter: "Ch. 5 — The Patients", bg: IMG.facility, mode: "grief",
    text: [
      "You black out for a second. When you open your eyes, you are four years old. Your mother is reading to you during a storm.",
      "Then the story changes. She is not reading a book. She is reading laboratory notes into your ear.",
      "'Subject J-14. Neurological receptivity: exceptional. Sedation: minimal.'",
      "You come back to yourself sweating. The memory is now yours to carry.",
    ],
    choices: [
      { label: "Continue toward Mom's office", next: "ch6_office",
        effect: fx({ addJournal: "MEMORY — Mother reading. She was recording me.",
                    setFlag: { memory_reading: true }, sanity: -10, composure: -14, corruption: +3 }) },
    ],
  },

  // ----- CHAPTER 6: Mother's Confession -----
  ch6_office: {
    act: 2, chapter: "Ch. 6 — Mother's Confession", bg: IMG.facility,
    text: [
      "Dr. Miriam Mercer's office. Photographs of you. Her doctoral thesis: 'On Substrates That Should Not Be.'",
      "A tape recorder clicks on by itself. Her voice — younger, terrified:",
      "'The Entity does not want a body. It wants a door. Jack is that door.'",
    ],
    choices: [
      { label: "Take her keycard and her final log", next: "ch7_archives",
        effect: fx({ addItem: "Miriam's Keycard", addJournal: "COLLECTIBLE — Miriam's Log #1: Jack is the door.",
                    setFlag: { hasKeycard: true, miriam_log_1: true, keepsake_recorder: true },
                    collectible: "miriam", sanity: -14, composure: -12, corruption: +2 }) },
      { label: "Smash the recorder. You don't want to know.", next: "ch7_archives",
        effect: fx({ addItem: "Miriam's Keycard", setFlag: { hasKeycard: true }, sanity: -22, composure: -18, vengeance: +2 }) },
    ],
  },

  // ----- CHAPTER 7: The Archives -----
  ch7_archives: {
    act: 2, chapter: "Ch. 7 — The Archives", bg: IMG.facility, mode: "stealth",
    text: [
      "Orpheus Biomedical's sealed archives. Physical files. The kind that cannot be deleted.",
      "You find a folder marked MERCER, J-14. Your birth certificate. Your neurological scans. Your first drawings — annotated by Voss himself.",
      "Dad kept working here to keep you contained. If the core dies, so does the wall around you.",
    ],
    choices: [
      { label: "Take the J-14 file. Keep the truth.", next: "ch8_garden",
        effect: fx({ addJournal: "COLLECTIBLE — Voss Recording #1: 'Subject Fourteen. He is the vessel we could not build.'",
                    setFlag: { voss_log_1: true, knowsTruth: true }, collectible: "voss", sanity: -18, composure: -14, corruption: +4 }) },
      { label: "Burn the file. Let no one else know what you are.", next: "ch8_garden",
        effect: fx({ setFlag: { burnedFile: true }, sanity: -8, composure: +6, vengeance: +2 }) },
    ],
  },

  // ----- CHAPTER 8: The Living Garden -----
  ch8_garden: {
    act: 2, chapter: "Ch. 8 — The Living Garden", bg: IMG.red,
    text: [
      "Botanical Research Wing. Roots have grown through the walls into human shapes.",
      "They react to sound. Loud movement makes them close hallways behind you.",
      "The Corruption is here. Its body has changed. It moves across the ceiling like something that never had bones.",
    ],
    choices: [
      { label: "Move slow. Use the UV emitters to burn a path.", next: "ch9_escape",
        effect: fx({ addJournal: "The plants sang to me. In her voice.", sanity: -12, composure: -8, hallucinate: "mother" }) },
      { label: "Run. Let the roots block it behind you.", next: "ch9_escape",
        effect: fx({ sanity: -18, composure: -14, vengeance: +2 }) },
    ],
  },

  // ----- CHAPTER 9: Failed Escape -----
  ch9_escape: {
    act: 2, chapter: "Ch. 9 — Failed Escape", bg: IMG.red, mode: "danger",
    text: [
      "Emergency transit platform. The maintenance train hisses awake.",
      "The Corruption tears the track apart with its bare mouths.",
      "You drop a maintenance crane on it. For a moment you believe you have killed it. It rises. Wetter. Taller. Angrier.",
    ],
    choices: [
      { label: "Turn back. Descend to the Requiem Core.", next: "ch10_chamber",
        effect: fx({ act: 3, addJournal: "I stopped running. I want it dead.",
                    setFlag: { turned_back: true }, sanity: -10, composure: -20, vengeance: +3, corruption: +2 }) },
      { label: "Turn back — for Mom, not for revenge.", next: "ch10_chamber",
        effect: fx({ act: 3, addJournal: "I stopped running. I want to bring her back.",
                    setFlag: { turned_back: true, forMother: true }, sanity: -8, composure: -14, corruption: +3 }) },
    ],
  },

  // ============================================================
  // ACT THREE — THE FINAL TRAP
  // ============================================================

  // ----- CHAPTER 10: The Original Chamber -----
  ch10_chamber: {
    act: 3, chapter: "Ch. 10 — The Original Chamber", bg: IMG.red, mode: "grief",
    text: [
      "The synchronization chamber. This is where Mom died.",
      "A preserved memory bleeds out of the walls. You watch it play like film on wet glass.",
      "Miriam pulls the emergency lever. Voss stops her. David arrives ten seconds too late. Miriam's last look is not for Voss. It is for the camera in the corner. It is for you, six years later.",
      "A fragment of her consciousness is still inside the Requiem network. You can feel it — like a hand against a fogged window.",
    ],
    choices: [
      { label: "Preserve her memory. It has weight.", next: "ch11_build",
        effect: fx({ setFlag: { preservedMiriam: true }, addJournal: "COLLECTIBLE — Miriam's Log #2 (Final).",
                    miriam_log_2: true, collectible: "miriam", composure: +18, sanity: +8 }) },
      { label: "Take her fragment as energy for the trap.", next: "ch11_build",
        effect: fx({ setFlag: { consumedMiriam: true }, addJournal: "I used what was left of her.", vengeance: +3, composure: -14 }) },
    ],
  },

  // ----- CHAPTER 11: Build the Requiem Trap -----
  ch11_build: {
    act: 3, chapter: "Ch. 11 — Build the Requiem Trap", bg: IMG.red,
    text: [
      "Dad's final blueprint. Overload the Requiem core while the vessel is inside the chamber.",
      "Four components: two power nodes (North and East), the sound lure, and the magnetic containment.",
      "The Corruption is in permanent hunt now. Safe rooms are failing. The building is eating itself.",
    ],
    choices: [
      { label: "Restore Power Node — North", next: "ch11_north",
        requires_missing_flag: "nodeN", effect: fx({ setFlag: { nodeN: true }, addJournal: "Node North online.", corruption: +2, sanity: -6 }) },
      { label: "Restore Power Node — East", next: "ch11_east",
        requires_missing_flag: "nodeE", effect: fx({ setFlag: { nodeE: true }, addJournal: "Node East online.", corruption: +2, sanity: -6 }) },
      { label: "Arm the magnetic containment", next: "ch11_magnets",
        requires_missing_flag: "magnets", effect: fx({ setFlag: { magnets: true }, addJournal: "Magnetic containment armed.", sanity: -4 }) },
      { label: "Go to the sound lure console", next: "ch12_lure", requires_all: ["nodeN", "nodeE", "magnets"] },
    ],
  },
  ch11_north: {
    act: 3, chapter: "Ch. 11 — Build the Requiem Trap", bg: IMG.red, mode: "stealth",
    text: [
      "The north catwalk. You reroute the coupling. Below you, the Corruption paces a circle, sniffing at the air.",
      "It looks up. Its face is not Voss anymore. It is nothing anymore.",
    ],
    choices: [
      { label: "Return to the chamber", next: "ch11_build", effect: fx({ sanity: -6 }) },
    ],
  },
  ch11_east: {
    act: 3, chapter: "Ch. 11 — Build the Requiem Trap", bg: IMG.red, mode: "stealth",
    text: [
      "The east node is submerged in something warm that shouldn't be warm.",
      "You reach in anyway. Dad would have. Mom did.",
      "The corruption mark on your arm brightens. You do not remember when you first noticed it.",
    ],
    choices: [
      { label: "Return to the chamber", next: "ch11_build",
        effect: fx({ sanity: -8, corruption: +6, hallucinate: "corruption" }) },
    ],
  },
  ch11_magnets: {
    act: 3, chapter: "Ch. 11 — Build the Requiem Trap", bg: IMG.red,
    text: [
      "The containment restraints wake up screaming. Old iron. Old current.",
      "They will hold it for maybe forty seconds.",
    ],
    choices: [
      { label: "Return to the chamber", next: "ch11_build", effect: fx({ sanity: -4 }) },
    ],
  },

  // ----- CHAPTER 12: Final Confrontation -----
  ch12_lure: {
    act: 3, chapter: "Ch. 12 — The Final Confrontation", bg: IMG.red, mode: "danger",
    text: [
      "The sound lure console. You may load one voice into the chamber speakers.",
      "Each will summon it. Each will do something else to it as well.",
    ],
    choices: [
      { label: "Use Dad's voice — it will enrage the vessel", next: "ch12_stage",
        effect: fx({ setFlag: { lure: "david" }, addJournal: "Chose Dad's voice. He would understand." }) },
      { label: "Use Mom's voice — the human inside may resist",
        requires_flag: "preservedMiriam", next: "ch12_stage",
        effect: fx({ setFlag: { lure: "miriam" }, addJournal: "Chose Mom's voice. Somewhere in there, Voss might listen." }) },
      { label: "Use your own voice — it will come immediately", next: "ch12_stage",
        effect: fx({ setFlag: { lure: "jack", usedOwnVoice: true }, corruption: +8, addJournal: "I called it in my own voice.", sanity: -15 }) },
    ],
  },
  ch12_stage: {
    act: 3, chapter: "Ch. 12 — The Final Confrontation", bg: IMG.red, mode: "danger",
    text: [
      "It comes. It always comes when called.",
      "It reaches the platform. Magnetic restraints slam shut. The chest of the vessel splits open — the Requiem core beneath its ribs is a pulsing violet sun.",
      "You climb to the console. Three energy lenses to align. Then the choice.",
    ],
    choices: [
      { label: "Align the lenses and reach the final console", next: "ch12_choice",
        effect: fx({ sanity: -8, composure: -12 }) },
    ],
  },
  ch12_choice: {
    act: 3, chapter: "Ch. 12 — The Final Confrontation", bg: IMG.red, mode: "danger",
    text: [
      "Three switches. Dad's blueprint marked only one.",
      "You hear Miriam somewhere in the machinery. Or the Entity wearing her. There is no way to know now.",
      "Choose.",
    ],
    choices: [
      // Ending routing is resolved dynamically in resolveNext based on flags/meters
      { label: "REQUIEM — Overload the core. Dad's plan.", next: "__ending__",
        effect: fx({ setFlag: { endingChoice: "requiem" } }) },
      { label: "MEMORY — Try to separate her from the Entity first",
        requires_flag: "preservedMiriam", next: "__ending__",
        effect: fx({ setFlag: { endingChoice: "memory" } }) },
      { label: "VENGEANCE — Overload EVERYTHING. Bury it all.",
        next: "__ending__", effect: fx({ setFlag: { endingChoice: "vengeance" } }) },
      { label: "ACCEPT — Let it in. Bring them home.",
        requires_flag: "ngPlusUnlocked", next: "__ending__",
        effect: fx({ setFlag: { endingChoice: "open" } }) },
    ],
  },

  // ============================================================
  // ENDINGS
  // ============================================================
  ending_requiem: {
    act: 3, chapter: "Ending — Requiem", bg: IMG.red, mode: "credits",
    text: [
      "The core inhales. Energy spikes impale the vessel. It does not scream. It unmakes.",
      "A last tendril reaches you. It burns a symbol into your forearm — a circle bisected by a wound.",
      "You escape through the drainage channel you entered as a boy.",
      "Police find you unconscious in the treeline at 6:04 AM. Halloween morning. Nobody believes what you saw.",
      "— DARK REQUIEM: THE CORRUPTION WITHIN —",
      "Canonical ending: REQUIEM.",
    ],
    choices: [ { label: "Post-credits scene", next: "post_credits" } ],
  },
  ending_memory: {
    act: 3, chapter: "Ending — Memory", bg: IMG.facility, mode: "credits",
    text: [
      "You use her fragment. For four impossible seconds, Voss's face returns.",
      "He is old. He is sorry.",
      "Miriam's voice says, 'Jack. Run.'",
      "You do. Both of them go out together, like a candle at the end of a long room.",
      "You walk out into a sunrise you cannot feel.",
      "— ENDING: MEMORY —",
      "You saved him. You lost her again. You will spend a long time deciding which of those mattered.",
    ],
    choices: [ { label: "Post-credits scene", next: "post_credits" } ],
  },
  ending_vengeance: {
    act: 3, chapter: "Ending — Vengeance", bg: IMG.red, mode: "credits",
    text: [
      "You overload everything. The building becomes a lantern, then a mouth, then nothing.",
      "When the officers reach you, you are calm. Uninjured. Watching the smoke.",
      "'Son, what happened in there?'",
      "You answer in two voices at once:",
      "'It's over.'",
      "The second voice, quieter, adds: 'It's beginning.'",
      "— ENDING: VENGEANCE —",
    ],
    choices: [ { label: "Post-credits scene", next: "post_credits" } ],
  },
  ending_open: {
    act: 3, chapter: "Ending — The Open Door", bg: IMG.red, mode: "credits",
    text: [
      "You do not overload it. You open it.",
      "Mom's voice, alive. Dad's voice, alive. Voss, quiet at last.",
      "You walk out at sunrise. Every dead security camera you pass turns to follow you.",
      "The Entity speaks through your mouth, gently:",
      "'We are finally going home.'",
      "— SECRET ENDING: THE OPEN DOOR —",
    ],
    choices: [ { label: "Post-credits scene", next: "post_credits" } ],
  },

  post_credits: {
    act: 3, chapter: "— Post-Credits —", bg: IMG.facility, mode: "credits",
    text: [
      "Several months later. Foster family. A quiet house.",
      "Your notebook is full of diagrams: circuitry, containment rings, the mark on your arm.",
      "The news anchor says Orpheus Biomedical denies ever operating in Blackwood County.",
      "You look up into the bathroom mirror to brush your teeth.",
      "For half a second, your reflection does not move. Then it smiles. Behind it, in the mirror only, stands the final form of The Corruption.",
      "You turn. The room is empty.",
      "In the mirror, your eyes are violet.",
      "A voice whispers: 'You did not destroy us. You brought us outside.'",
      "— TO BE CONTINUED IN DARK REQUIEM II: THE AWAKENING —",
    ],
    choices: [ { label: "Return to the beginning", next: "__restart__" } ],
  },
};

// ============================================================
// Dynamic routing & ending resolution
// ============================================================

function pickEnding(state) {
  const choice = state.flags?.endingChoice;
  if (choice === "open") return "ending_open";
  if (choice === "memory" && state.flags?.preservedMiriam) return "ending_memory";
  if (choice === "vengeance") return "ending_vengeance";
  // Requiem is canon
  return "ending_requiem";
}

export function resolveNext(sceneId, choice, state) {
  if (choice.next === "__ending__") {
    return pickEnding(state);
  }
  return choice.next;
}

// Flags marked as requires_missing_flag hide when set (so build hub filters used steps)
export function isChoiceAvailable(choice, state) {
  if (choice.requires) {
    for (const req of choice.requires) if (!state.inventory.includes(req)) return false;
  }
  if (choice.requires_item && !state.inventory.includes(choice.requires_item)) return false;
  if (choice.requires_flag && !state.flags?.[choice.requires_flag]) return false;
  if (choice.requires_missing_flag && state.flags?.[choice.requires_missing_flag]) return false;
  if (choice.requires_all) {
    for (const f of choice.requires_all) if (!state.flags?.[f]) return false;
  }
  return true;
}
